import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  LogOut,
  Menu as MenuIcon,
  MoreHorizontal,
  UserRound,
  X,
} from 'lucide-react';
import { ROLE_LABELS } from '@mouldtrack/shared';
import { useAuth } from '../../auth/AuthProvider.js';
import { useSafeSignOut } from '../../auth/SafeSignOut.js';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../ui/menu.js';
import { SyncIndicator } from './SyncIndicator.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { NAV_SECTIONS, type NavItem } from './navigation.js';
import { OfflineBanner } from './OfflineBanner.js';

/**
 * The shell.
 *
 * Desktop gets a persistent sidebar because managers keep many screens in play.
 * Phones get a bottom tab bar because the top of a 6-inch screen is out of thumb
 * reach - the same navigation, placed where the hand actually is.
 */
export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // Route change closes the drawer; otherwise tapping a link leaves it hanging
  // open over the page it just navigated to.
  useEffect(() => setMobileNavOpen(false), [location.pathname]);

  return (
    <div className="flex h-dvh flex-col bg-background">
      <OfflineBanner />

      <div className="flex min-h-0 flex-1">
        <DesktopSidebar />

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onOpenNav={() => setMobileNavOpen(true)} />
          <main className="min-h-0 flex-1 overflow-y-auto pb-20 lg:pb-0">
            {/* Keyed on the path: leaving a broken screen resets it. */}
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>

      <MobileTabBar />
      <MobileDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
    </div>
  );
}

// --- desktop sidebar ---

function DesktopSidebar() {
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-surface lg:flex">
      <div className="flex h-14 items-center gap-2.5 px-4">
        <img src="/icon.svg" alt="" className="size-7 rounded-lg" />
        <span className="text-[15px] font-semibold tracking-tight">MouldTrack</span>
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {NAV_SECTIONS.map((section) => (
          <NavSectionBlock key={section.id} section={section} />
        ))}
      </nav>
      <UserCard />
    </aside>
  );
}

function NavSectionBlock({ section }: { section: (typeof NAV_SECTIONS)[number] }) {
  const { allows } = useAuth();
  const location = useLocation();
  const visible = section.items.filter(
    (i) => !i.permission || allows(i.permission.action, i.permission.resource),
  );

  // Collapse only a section long enough to be in the way. Master Data used to be
  // eleven entries and needed it; it is one now, and a disclosure triangle
  // hiding a single link just makes the link impossible to find.
  const collapsible = visible.length > 4;
  const containsActive = visible.some((i) => location.pathname.startsWith(i.to));
  const [open, setOpen] = useState(containsActive);
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);

  // Bailing out has to happen *after* the hooks: an early return above them
  // changes the hook count between renders the moment a permission flips, and
  // React answers that by tearing down the tree - another white screen.
  if (visible.length === 0) return null;

  return (
    <div className="mt-4 first:mt-2">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-subtle-foreground hover:text-muted-foreground"
        >
          {section.label}
          <ChevronDown className={cn('size-3.5 transition-transform', !open && '-rotate-90')} />
        </button>
      ) : (
        <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-subtle-foreground">
          {section.label}
        </p>
      )}

      {open || !collapsible ? (
        <ul className="mt-1 space-y-0.5">
          {visible.map((item) => (
            <li key={item.to}>
              <SidebarLink item={item} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      data-nav
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
          isActive
            ? 'bg-primary-subtle font-medium text-primary'
            : 'text-muted-foreground hover:bg-surface-sunken hover:text-foreground',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function UserCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const signOut = useSafeSignOut();
  if (!user) return null;
  return (
    <div className="border-t border-border p-3">
      {signOut.dialog}
      <Menu
        align="start"
        trigger={
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-surface-sunken"
          >
            <Avatar name={user.name} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{user.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {ROLE_LABELS[user.role]}
              </span>
            </span>
            <MoreHorizontal className="size-4 shrink-0 text-muted-foreground" />
          </button>
        }
      >
        <MenuLabel>{user.email}</MenuLabel>
        <MenuSeparator />
        <MenuItem icon={<UserRound />} onSelect={() => navigate('/account')}>
          Your account
        </MenuItem>
        <MenuItem icon={<LogOut />} destructive onSelect={signOut.request}>
          Sign out
        </MenuItem>
      </Menu>
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();
  return (
    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-subtle text-xs font-semibold text-primary">
      {initials}
    </span>
  );
}

// --- top bar ---

function TopBar({ onOpenNav }: { onOpenNav: () => void }) {
  return (
    <header className="safe-t sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface/85 px-3 backdrop-blur-md lg:px-5">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenNav} aria-label="Open menu">
        <MenuIcon className="size-5" />
      </Button>

      <div className="flex items-center gap-2 lg:hidden">
        <img src="/icon.svg" alt="" className="size-6 rounded-md" />
        <span className="text-sm font-semibold">MouldTrack</span>
      </div>

      <div className="flex-1" />
      <SyncIndicator />
    </header>
  );
}

// --- mobile ---

/** The four destinations worth a permanent thumb-reachable tab. */
function MobileTabBar() {
  const { allows } = useAuth();
  const items = NAV_SECTIONS[0]?.items.filter(
    (i) => !i.permission || allows(i.permission.action, i.permission.resource),
  );
  if (!items?.length) return null;

  return (
    <nav className="safe-b fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface/95 backdrop-blur-md lg:hidden">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            data-nav
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-0.5 px-1 py-2 text-[11px] font-medium transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground',
              )
            }
          >
            <Icon className="size-5" aria-hidden />
            {item.shortLabel ?? item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

/** Everything that does not fit the tab bar - chiefly the eleven master tables. */
function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const signOut = useSafeSignOut();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button
        type="button"
        aria-label="Close menu"
        className="overlay absolute inset-0 bg-black/45"
        data-state="open"
        onClick={onClose}
      />
      <div
        className="safe-t absolute inset-y-0 left-0 flex w-72 flex-col border-r border-border bg-surface shadow-2xl"
        role="dialog"
        aria-label="Navigation"
      >
        <div className="flex h-14 items-center gap-2.5 px-4">
          <img src="/icon.svg" alt="" className="size-7 rounded-lg" />
          <span className="text-[15px] font-semibold">MouldTrack</span>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="size-5" />
          </Button>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
          {NAV_SECTIONS.map((section) => (
            <NavSectionBlock key={section.id} section={section} />
          ))}
        </nav>

        {user ? (
          <div className="safe-b border-t border-border p-3">
            <div className="mb-2 px-1">
              <p className="truncate text-sm font-medium">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</p>
            </div>
            <Button variant="ghost" className="mb-1 w-full justify-start" asChild>
              <Link to="/account" onClick={onClose}>
                <UserRound className="size-4" /> Your account
              </Link>
            </Button>
            {signOut.dialog}
            <Button variant="secondary" className="w-full" onClick={signOut.request} loading={signOut.busy}>
              <LogOut className="size-4" /> Sign out
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
